/**
 * 愛媛県の背景ツアー「愛媛めぐり」コンテンツ。
 *
 * 軸は一日の時間。夜明けの石鎚山から、夜の道後温泉本館へ。
 *
 * しまなみ海道の橋について。
 *  多々羅大橋は広島（尾道市）ですでに使っている。愛媛側は来島海峡大橋を使う。
 *  「世界初の三連吊橋」は道路管理者（JB本四高速）の公式に断定形で書かれているので使える。
 *
 * 道後温泉本館について。
 *  「日本最古の温泉」は公式自身が「といわれる」という伝聞形。使わない。
 *  書けるのは、平成6年12月に公衆浴場として初めて国の重要文化財に指定されたという事実。
 *  保存修理工事の進捗など、時間で変わることは書かない。
 *  塔屋の名称「振鷺閣」は公式で確認できなかったので使わない。「塔屋」と書く。
 *
 * 別子銅山 東平について。
 *  鉱山は島根（石見銀山）と岡山（吹屋）ですでに2か所使っている。
 *  東平は「山中の巨大な石積みの産業遺構」として、坑道の絵とも赤い町並みとも分ける。
 *  「東洋のマチュピチュ」は伝聞的な呼称。使わない。開坑年・閉山年も公式で確認できなかった。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 下灘駅の「日本一海に近い駅」は公式に記述がない。「無人駅」も未確認。どちらも書かない。
 *  - 佐田岬の「日本一細長い半島」は国・県の断定形が取れない。「細長い半島」にとどめる。
 *  - 石鎚山の「西日本最高峰」は県観光物産協会の断定形。国の資料では未確認だが、その出典で書く。
 *  - 夏目漱石や正岡子規の作品の引用、登場人物には触れない。
 *  - 松山城のロープウェイ・リフトの運行情報は未確認かつ時間で変わる。書かない。
 */

import type { StageTour } from '../types';

export const ehimeSceneryTour: StageTour = {
  scenes: [
    {
      id: 'ishizuchisan',
      name: '石鎚山',
      area: '西条市・久万高原町・秋の夜明け',
      image: '/assets/scenery/ehime/scenes/01_ishizuchisan.webp',
      hotspots: [
        {
          id: 'ishizuchi-tengudake',
          x: 0.46,
          y: 0.36,
          title: '天狗岳は西日本最高峰',
          body: '石鎚山の最高峰は標高1982メートルの天狗岳で、西日本最高峰とされています。天狗岳と、石鎚神社山頂社のある弥山、南尖峰の一連の山々をまとめて石鎚山と呼びます。秋には赤く染まる天狗岳を見ようと、多くの人が訪れます。',
          source: 'https://www.iyokannet.jp/spot/413',
          mapQuery: '石鎚山 天狗岳',
        },
        {
          id: 'ishizuchi-kusari',
          x: 0.74,
          y: 0.52,
          title: '鎖をたよりに垂直の岩を登る',
          body: '石鎚山の登山道には全国でも珍しい鎖場があり、垂直に切り立った崖を、鎖をたよりに登っていきます。迂回路も整備されていて、体力に合わせてルートを選べます。',
          source: 'https://www.iyokannet.jp/spot/413',
          mapQuery: '石鎚山 鎖場',
        },
        {
          id: 'ishizuchi-reizan',
          x: 0.22,
          y: 0.48,
          title: '日本七霊山のひとつ',
          body: '石鎚山は、古くから山岳信仰の山として知られる日本七霊山のひとつです。登山道は、久万高原町の土小屋から尾根づたいに歩くコースや、西条市から石鎚登山ロープウェイを使うコースなど、経験に合わせて選べます。',
          source: 'https://www.iyokannet.jp/spot/413',
          mapQuery: '石鎚山 西条市',
        },
      ],
    },
    {
      id: 'yusu-mizugaura',
      name: '遊子水荷浦の段畑',
      area: '宇和島市・早春の朝',
      image: '/assets/scenery/ehime/scenes/02_yusu_mizugaura.webp',
      hotspots: [
        {
          id: 'yusu-keikan',
          x: 0.46,
          y: 0.44,
          title: '国の重要文化的景観に選定',
          body: '遊子水荷浦の段畑は「日本農村百景」に選ばれたほか、平成19年には国の重要文化的景観にも選定されました。石垣は年月をかけて一つ一つ丹念に手で積まれており、まるで遺跡のようにも見えます。',
          source: 'https://www.iyokannet.jp/spot/656',
          mapQuery: '遊子水荷浦の段畑',
        },
        {
          id: 'yusu-30do',
          x: 0.74,
          y: 0.34,
          title: '傾斜30度を超える石垣の段',
          body: '斜面の傾きは30度を超え、頂上は海抜80メートルにもなります。下から見上げると、まるで天に上る階段のようです。',
          source: 'https://www.iyokannet.jp/spot/656',
          mapQuery: '宇和島市 遊子 段畑',
        },
        {
          id: 'yusu-jagaimo',
          x: 0.22,
          y: 0.5,
          title: '段に植わるのはじゃがいも',
          body: '石垣の段に植えられている主な作物はじゃがいもです。収穫前の3月中旬は、じゃがいもの緑が映える見頃。中腹からは宇和島湾を望み、養殖のいけすや行き交う船が見えます。',
          source: 'https://www.iyokannet.jp/spot/656',
          mapQuery: '遊子水荷浦 段畑 じゃがいも',
        },
      ],
    },
    {
      id: 'uchiko',
      name: '内子 八日市護国の町並み',
      area: '内子町・午前',
      image: '/assets/scenery/ehime/scenes/03_uchiko.webp',
      hotspots: [
        {
          id: 'uchiko-judenken',
          x: 0.46,
          y: 0.42,
          title: '昭和57年に選定された製蝋町',
          body: '八日市護国地区は昭和57年4月17日に国の重要伝統的建造物群保存地区に選定されました。保存地区の面積は3.5ヘクタール、種別は製蝋町です。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '八日市護国 重要伝統的建造物群保存地区',
        },
        {
          id: 'uchiko-600m',
          x: 0.74,
          y: 0.34,
          title: 'およそ600メートル続く旧街道',
          body: '旧街道沿いに、江戸末期から明治、大正にかけて建てられた豪壮な商家や土蔵、町家がおよそ600メートルにわたって軒を連ねます。建物ごとに独特の装飾が凝らされ、今も人が住み続けています。',
          source: 'https://www.iyokannet.jp/spot/613',
          mapQuery: '内子町 八日市護国の町並み',
        },
        {
          id: 'uchiko-mokuro',
          x: 0.22,
          y: 0.5,
          title: '木蝋で栄えた町の大邸宅',
          body: '町並みの一角に建つ上芳我邸は、内子の木蝋生産の中心となった芳我家の分家の屋敷です。主屋は製蝋業が最盛期を迎えた明治27年に上棟されました。10棟が平成2年9月11日に重要文化財に指定されています。',
          source: 'https://www.town.uchiko.ehime.jp/site/hozonsenta/kamihaga.html',
          mapQuery: '内子 木蝋資料館 上芳我邸',
        },
      ],
    },
    {
      id: 'matsuyama-jo',
      name: '松山城',
      area: '松山市・昼前',
      image: '/assets/scenery/ehime/scenes/04_matsuyama_jo.webp',
      hotspots: [
        {
          id: 'matsuyama-12tenshu',
          x: 0.46,
          y: 0.36,
          title: '現存12天守のひとつ',
          body: '松山城は、江戸時代までに建てられた現存12天守のひとつを持つ城で、21棟が国の重要文化財に指定されています。加藤嘉明が1602年から築き始め、完成までにおよそ四半世紀を要しました。',
          source: 'https://www.matsuyamajo.jp/guide/matsuyamacastle.html',
          mapQuery: '松山城 天守',
        },
        {
          id: 'matsuyama-renritsu',
          x: 0.74,
          y: 0.52,
          title: '連立式という構え',
          body: '連立式天守とは、天守・小天守・櫓を四方に配置し、渡櫓でつないだ形式をいいます。建物で仕切られた中庭ができるのが特徴で、現存12天守の中では姫路城と同じ構成です。',
          source: 'https://www.matsuyamajo.jp/discover/tenshu.html',
          mapQuery: '松山城 連立式天守 本壇',
        },
        {
          id: 'matsuyama-katsuyama',
          x: 0.22,
          y: 0.48,
          title: '南北二つの峰を埋めた本丸',
          body: '本丸広場の標高は132メートル。もともと南北に二つの峰があり、その間を埋め立てて本丸の敷地をつくりました。',
          source: 'https://www.matsuyamajo.jp/discover/trivia.html',
          mapQuery: '松山城 勝山 本丸',
        },
      ],
    },
    {
      id: 'kurushima-ohashi',
      name: '来島海峡大橋',
      area: '今治市・正午',
      image: '/assets/scenery/ehime/scenes/05_kurushima_ohashi.webp',
      hotspots: [
        {
          id: 'kurushima-sanren',
          x: 0.46,
          y: 0.42,
          title: '世界初の三連吊橋',
          body: '来島海峡大橋は、来島海峡第一大橋・第二大橋・第三大橋という三つの吊橋の総称です。建設にあたって世界初の三連吊橋が採用されました。総延長は4.1キロメートルに及びます。',
          source: 'https://www.jb-honshi.co.jp/shimanami/about/shimanami/p07.html',
          mapQuery: '来島海峡大橋',
        },
        {
          id: 'kurushima-4km',
          x: 0.74,
          y: 0.34,
          title: '大島と今治を結ぶおよそ4キロ',
          body: '橋は大島と今治の間、およそ4キロメートルの来島海峡に架かります。第一大橋は960メートル、第二大橋は1515メートル、第三大橋は1570メートルで、三本がつながって一続きの道になります。',
          source: 'https://www.jb-honshi.co.jp/shimanami/about/shimanami/p07.html',
          mapQuery: '来島海峡第三大橋',
        },
        {
          id: 'kurushima-kirosan',
          x: 0.22,
          y: 0.5,
          title: '大島南端の展望台から見下ろす',
          body: '大島の南端、亀老山の頂上にある展望公園からは、来島海峡大橋を含む360度の眺望が開けます。展望台は地中に埋め込むように造られ、上には木が植えられています。',
          source: 'https://www.iyokannet.jp/spot/191',
          mapQuery: '亀老山展望公園',
        },
      ],
    },
    {
      id: 'tonaru',
      name: '別子銅山 東平',
      area: '新居浜市・午後',
      image: '/assets/scenery/ehime/scenes/06_tonaru.webp',
      hotspots: [
        {
          id: 'tonaru-750m',
          x: 0.46,
          y: 0.44,
          title: '標高750メートルの山中',
          body: '東平は新居浜の深い山の中に残る別子銅山の産業遺産群です。標高750メートル前後のこの場所からは、天気の良い日に新居浜の市街地と、その先の瀬戸内海まで見渡せます。',
          source: 'https://www.iyokannet.jp/spot/2822',
          mapQuery: '東平 別子銅山',
        },
        {
          id: 'tonaru-chokoko',
          x: 0.74,
          y: 0.34,
          title: '森に残る貯鉱庫の跡',
          body: '森の中には、東平貯鉱庫跡が残っています。実際に使われてきた坑道は鉱山運搬車両の展示場として保存され、東平歴史資料館では往時の町並みや学校の様子を再現したジオラマが見られます。',
          source: 'https://www.iyokannet.jp/spot/2822',
          mapQuery: '東平貯鉱庫跡',
        },
        {
          id: 'tonaru-sakudo',
          x: 0.22,
          y: 0.5,
          title: '索道の基地と第三通洞',
          body: '東平には、貯鉱庫跡や索道基地跡の石積み、第三通洞跡が残っています。かまど跡が残る採掘集落の跡もあります。',
          source: 'https://www.city.niihama.lg.jp/soshiki/kankou/tonaru.html',
          mapQuery: '新居浜市 東平ゾーン',
        },
      ],
    },
    {
      id: 'ozu-jo',
      name: '大洲城と肱川',
      area: '大洲市・夕方前',
      image: '/assets/scenery/ehime/scenes/07_ozu_jo.webp',
      hotspots: [
        {
          id: 'ozu-mokuzo',
          x: 0.46,
          y: 0.38,
          title: '四層の天守を木造で復元',
          body: '大洲城の天守は、古い記録や江戸時代の木組模型をもとに、平成16年に四層の木造建築として復元されました。天守からは大洲の城下町が360度見渡せます。',
          source: 'https://www.iyokannet.jp/spot/505',
          mapQuery: '大洲城',
        },
        {
          id: 'ozu-yagura',
          x: 0.74,
          y: 0.5,
          title: '四棟の櫓は重要文化財',
          body: '明治21年に天守は取り壊されましたが、四棟の櫓は解体をまぬがれ、いずれも国の重要文化財に指定されています。平成16年に復元された天守と、これらの櫓が同じ敷地に並んでいます。',
          source: 'https://www.iyokannet.jp/spot/505',
          mapQuery: '大洲城 台所櫓',
        },
        {
          id: 'ozu-hijikawa',
          x: 0.22,
          y: 0.48,
          title: '城から見下ろす肱川',
          body: '丘の上に建つ城からは、眼下に城下町と肱川を見下ろせます。JR四国の観光列車「伊予灘ものがたり」が鉄橋を渡るときには、本丸からのぼり旗を振って歓迎しています。',
          source: 'https://www.iyokannet.jp/spot/505',
          mapQuery: '大洲城 肱川',
        },
      ],
    },
    {
      id: 'sadamisaki',
      name: '佐田岬灯台',
      area: '伊方町・夕方',
      image: '/assets/scenery/ehime/scenes/08_sadamisaki.webp',
      hotspots: [
        {
          id: 'sadamisaki-tanto',
          x: 0.46,
          y: 0.4,
          title: '細長い半島の突端に立つ',
          body: '佐田岬灯台は、細長く伸びる佐田岬半島の先端の岸壁にそびえ立つ白亜の灯台です。所在地は伊方町正野で、駐車場から歩いて向かいます。',
          source: 'https://www.iyokannet.jp/spot/634',
          mapQuery: '佐田岬灯台',
        },
        {
          id: 'sadamisaki-kyushu',
          x: 0.74,
          y: 0.5,
          title: '晴れた日には九州が見える',
          body: '四国の最西端にあって、晴れた日には豊予海峡を隔てて九州を遠望できます。突端では潮風を受けながら、青い海を眼下に見下ろせます。',
          source: 'https://www.iyokannet.jp/spot/634',
          mapQuery: '佐田岬 豊予海峡',
        },
        {
          id: 'sadamisaki-tenbodai',
          x: 0.22,
          y: 0.48,
          title: '四国最西端の展望台',
          body: '佐田岬半島の先端部には、四国最西端の展望台である椿山展望台があります。灯台と豊予海峡、大分県の佐賀関半島を同時に一望でき、冬から早春にかけては山の斜面がヤブツバキで彩られます。',
          source: 'https://www.iyokannet.jp/spot/1661',
          mapQuery: '佐田岬半島 椿山展望台',
        },
      ],
    },
    {
      id: 'shimonada-eki',
      name: '下灘駅',
      area: '伊予市双海町・日没',
      image: '/assets/scenery/ehime/scenes/09_shimonada_eki.webp',
      hotspots: [
        {
          id: 'shimonada-iyonada',
          x: 0.46,
          y: 0.44,
          title: 'ホームの先に伊予灘',
          body: '所在地は伊予市双海町大久保。ホームの先に広がる景観で知られ、鉄道ファンの間では説明のいらないほど有名な駅です。昼間は海面が日の光できらめきます。',
          source: 'https://www.iyokannet.jp/spot/3580',
          mapQuery: 'JR下灘駅',
        },
        {
          id: 'shimonada-yuhi',
          x: 0.74,
          y: 0.36,
          title: '夕日に光る黄金の海',
          body: '夕暮れには、夕日に照らされた黄金の海が光り輝きます。駅の前がきれいに開けていて、伊予灘が一面に広がります。',
          source: 'https://www.iyokannet.jp/spot/3580',
          mapQuery: '下灘駅 夕日',
        },
        {
          id: 'shimonada-poster',
          x: 0.22,
          y: 0.5,
          title: 'ポスターや映像に選ばれた駅',
          body: '青春18きっぷのポスターに採用されたほか、これまでに数々の映像作品の舞台になってきました。一度は降りてみたい駅として知られています。',
          source: 'https://www.iyokannet.jp/spot/3580',
          mapQuery: '下灘駅 双海町',
        },
      ],
    },
    {
      id: 'dogo-onsen',
      name: '道後温泉本館',
      area: '松山市・夜',
      image: '/assets/scenery/ehime/scenes/10_dogo_onsen.webp',
      hotspots: [
        {
          id: 'dogo-juyobunkazai',
          x: 0.46,
          y: 0.44,
          title: '公衆浴場として初の重要文化財',
          body: '道後温泉本館は、平成6年12月に、公衆浴場としては初めて国の重要文化財に指定されました。平成31年1月から続いた保存修理工事は、令和6年12月に完了しています。',
          source: 'https://dogo.jp/onsen/honkan',
          mapQuery: '道後温泉本館',
        },
        {
          id: 'dogo-shirasagi',
          x: 0.74,
          y: 0.28,
          title: '塔屋の上に立つ白鷺',
          body: '屋根の上に突き出た塔屋の上には、本館のシンボルである白鷺が据えられています。明治27年の改築時から続く刻太鼓は、日本の音風景100選に選ばれています。',
          source: 'https://dogo.jp/onsen/honkan',
          mapQuery: '道後温泉本館 塔屋',
        },
        {
          id: 'dogo-yushinden',
          x: 0.22,
          y: 0.5,
          title: '皇室専用の浴室がある',
          body: '本館の中には又新殿と呼ばれる皇室専用の浴室があり、日本で唯一のものとされています。玉座の間など、贅を尽くした空間が設けられています。',
          source: 'https://dogo.jp/onsen/honkan',
          mapQuery: '道後温泉本館 又新殿',
        },
      ],
    },
  ],
};
